/**
 * Tap to aim, drag to turn (UI-008, owner, 2026-09-25, "like 8 Ball Pool").
 *
 * Aiming used to point the cue at the finger for as long as it was down. On a phone
 * the finger hides the spot it is pointing at, and every wobble swings the whole shot.
 * Now one touch is either:
 *
 * - a **tap** (lifted without moving more than `TAP_SLOP_PX`): aim at where it landed,
 *   as before; or
 * - a **drag**, which never snaps the aim to the finger. The aim turns from where it
 *   already was, by the angle the finger sweeps around the cue ball. Start anywhere.
 *   Further from the cue ball, the same finger movement is a smaller turn, so a
 *   player who wants fine control drags out near the rail.
 *
 * UI-023 (owner, 2026-10-12): the turn is a fraction of the angle swept, not all of it.
 * One-to-one was too fast, above all on a phone, where a small screen makes a short
 * swipe a wide sweep. A finger turns the aim half as far; a mouse, three quarters.
 *
 * Angles are measured in TABLE coordinates, and the turn is applied in table
 * coordinates too, so the result is right in portrait and landscape alike without
 * `screenTurnSign` (L28, L32): the finger and the aim turn the same way on screen
 * because they are the same rotation.
 */
import { directionTo, rotate, type Direction } from './direction.ts';

/** How far a finger may wander, in CSS pixels, and still count as a tap. */
export const TAP_SLOP_PX = 10;

/** UI-023: how far the aim turns for each degree the pointer sweeps round the cue ball. */
export const AIM_DRAG_RATIO = { touch: 0.5, mouse: 0.75 } as const;

/** The ratio for a pointer: a finger (or anything not a mouse or pen) gets the touch one. */
export function aimDragRatio(pointerType: string): number {
  return pointerType === 'mouse' || pointerType === 'pen'
    ? AIM_DRAG_RATIO.mouse
    : AIM_DRAG_RATIO.touch;
}

export interface TablePoint {
  readonly xM: number;
  readonly yM: number;
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface AimGesture {
  /** The aim to show now, or undefined while it is still possibly a tap. */
  move(screen: ScreenPoint, table: TablePoint): Direction | undefined;
  /** The finger lifted: a tap's aim, or undefined after a drag (its aim already applied). */
  release(): Direction | undefined;
}

/** An angle difference folded into (−π, π], so crossing "behind" the ball is not a 360° spin. */
function wrap(radians: number): number {
  let r = radians;
  while (r > Math.PI) r -= 2 * Math.PI;
  while (r <= -Math.PI) r += 2 * Math.PI;
  return r;
}

/**
 * Start a gesture. `deadZoneM`: inside this distance of the cue ball the finger's
 * angle is too jumpy to use, so movement there is ignored rather than spinning the aim.
 * `ratio`: how far the aim turns per angle swept (`aimDragRatio`).
 */
export function startAimGesture(
  aim: Direction | undefined,
  cue: TablePoint,
  downScreen: ScreenPoint,
  downTable: TablePoint,
  deadZoneM: number,
  ratio: number,
): AimGesture {
  let dragging = false;
  let turned = 0;

  const angleOf = (p: TablePoint): number | undefined => {
    const dx = p.xM - cue.xM;
    const dy = p.yM - cue.yM;
    return dx * dx + dy * dy < deadZoneM * deadZoneM ? undefined : Math.atan2(dy, dx);
  };
  let lastAngle = angleOf(downTable);

  return {
    move(screen, table) {
      if (!dragging) {
        const sx = screen.x - downScreen.x;
        const sy = screen.y - downScreen.y;
        if (sx * sx + sy * sy < TAP_SLOP_PX * TAP_SLOP_PX) return undefined;
        dragging = true;
      }
      const angle = angleOf(table);
      if (angle === undefined) {
        // Over the cue ball: hold the aim, and pick the angle up afresh on the way out.
        lastAngle = undefined;
      } else {
        if (lastAngle !== undefined) turned += wrap(angle - lastAngle);
        lastAngle = angle;
      }
      if (aim === undefined) return undefined;
      // Always from the aim the drag started with, so rounding never accumulates.
      return rotate(aim, turned * ratio);
    },

    release() {
      return dragging ? undefined : directionTo(cue.xM, cue.yM, downTable.xM, downTable.yM);
    },
  };
}
